import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Header, Footer } from "@/components/SiteChrome";
import { ProductTile } from "@/components/ProductTile";
import api from "@/lib/api";

const CATEGORIES = ["Lighting", "Recovery", "Storage", "Armor", "Wheels & Tires", "Exterior"];

export default function Shop() {
    const [params, setParams] = useSearchParams();
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState(params.get("q") || "");
    const cat = params.get("category") || "All";
    const sort = params.get("sort") || "featured";

    const setCat = (c) => {
        const p = new URLSearchParams(params);
        if (c === "All") p.delete("category"); else p.set("category", c);
        setParams(p);
    };
    const setSort = (s) => {
        const p = new URLSearchParams(params);
        if (s === "featured") p.delete("sort"); else p.set("sort", s);
        setParams(p);
    };

    useEffect(() => {
        setLoading(true);
        const qs = { limit: 300 };
        if (cat !== "All") qs.category = cat;
        if (q) qs.q = q;
        if (sort) qs.sort = sort;
        api.get("/products", { params: qs })
            .then(({ data }) => setProducts(data.items))
            .finally(() => setLoading(false));
    }, [cat, sort, q]);

    return (
        <div className="min-h-screen bg-[hsl(var(--dust-50))]" data-testid="shop-page">
            <Header />
            <div className="border-b-2 border-[hsl(var(--ink))] bg-[hsl(var(--dust-300))] text-[hsl(var(--dust-50))]">
                <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6">
                    <div className="font-mono-brand text-xs uppercase tracking-[0.25em] text-[hsl(var(--dust-100))]">All gear</div>
                    <h1 className="mt-2 font-display text-5xl uppercase italic md:text-7xl">The full kit.</h1>
                </div>
            </div>

            <div className="mx-auto grid max-w-7xl gap-10 px-4 py-12 sm:px-6 lg:grid-cols-[240px_1fr]">
                <aside className="space-y-8">
                    <div>
                        <div className="text-base font-bold uppercase tracking-wider text-[hsl(var(--forest))] font-heading">Search</div>
                        <input
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder="SKU, fitment, name…"
                            className="mt-2 w-full border border-[hsl(var(--ink))] bg-transparent px-3 py-2 font-mono-brand text-sm placeholder:text-[hsl(var(--ink-soft))] focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))]"
                            data-testid="shop-search"
                        />
                    </div>
                    <div>
                        <div className="text-base font-bold uppercase tracking-wider text-[hsl(var(--forest))] font-heading">Category</div>
                        <ul className="mt-3 space-y-1">
                            {["All", ...CATEGORIES].map((c) => (
                                <li key={c}>
                                    <button
                                        onClick={() => setCat(c)}
                                        className={`block w-full border-l-4 py-1.5 pl-3 text-left font-heading text-base font-bold uppercase tracking-wider transition-colors ${
                                            cat === c
                                                ? "border-[hsl(var(--primary))] text-[hsl(var(--ink))]"
                                                : "border-transparent text-[hsl(var(--ink-soft))] hover:text-[hsl(var(--ink))]"
                                        }`}
                                        data-testid={`shop-cat-${c.toLowerCase().replace(/\s|&/g, "")}`}
                                    >
                                        {c}
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </div>
                    <div>
                        <div className="text-base font-bold uppercase tracking-wider text-[hsl(var(--forest))] font-heading">Sort</div>
                        <select
                            value={sort}
                            onChange={(e) => setSort(e.target.value)}
                            className="mt-2 w-full border border-[hsl(var(--ink))] bg-transparent px-3 py-2 font-mono-brand text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))]"
                            data-testid="shop-sort"
                        >
                            <option value="featured">Featured</option>
                            <option value="low">Price: low → high</option>
                            <option value="high">Price: high → low</option>
                        </select>
                    </div>
                </aside>

                <div>
                    <div className="mb-6 flex items-center justify-between font-mono-brand text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))]">
                        <span data-testid="shop-count">{products.length} parts</span>
                        <span>{cat === "All" ? "All categories" : cat}</span>
                    </div>
                    {loading ? (
                        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
                            {Array.from({ length: 6 }).map((_, i) => (
                                <div key={i} className="animate-pulse border border-[hsl(var(--dust-300))] bg-card p-4">
                                    <div className="aspect-square bg-[hsl(var(--dust-100))]" />
                                    <div className="mt-3 h-4 w-1/2 bg-[hsl(var(--dust-100))]" />
                                    <div className="mt-2 h-3 w-3/4 bg-[hsl(var(--dust-100))]" />
                                </div>
                            ))}
                        </div>
                    ) : products.length === 0 ? (
                        <div className="border-2 border-dashed border-[hsl(var(--dust-300))] p-12 text-center">
                            <div className="font-display text-3xl uppercase italic text-[hsl(var(--ink))]">No parts match.</div>
                            <p className="mt-2 text-[hsl(var(--ink-soft))]">Try a different category or clear the search.</p>
                        </div>
                    ) : (
                        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
                            {products.map((p) => <ProductTile key={p.slug} p={p} />)}
                        </div>
                    )}
                </div>
            </div>

            <Footer />
        </div>
    );
}
